import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { invoiceService, meService } from '../services/api';
import { Invoice } from '../types';
import { paymentTypeLabel } from '../utils/paymentDisplay';
import {
  formatGuestMoney,
  guestInvoicePeriodLabel,
  guestInvoiceSummary,
  isGuestStayInvoice,
} from '../utils/guestInvoiceDisplay';
import { toast } from 'react-toastify';
import './MyInvoices.css';

const MyInvoices: React.FC = () => {
  const { user, hostelScopeKey, isResident } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      setLoading(true);
      void loadData();
    }
  }, [user?.id, hostelScopeKey]);

  const loadData = async () => {
    try {
      const data = await meService.getMyInvoices();
      setInvoices(data);
    } catch {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoice: Invoice) => {
    try {
      const blob = await invoiceService.downloadInvoice(invoice.id, invoice.userId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice downloaded successfully');
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  const totalPaid = invoices.reduce((sum, inv) => sum + parseFloat(inv.paidAmount || '0'), 0);
  const totalPending = invoices.reduce((sum, inv) => sum + parseFloat(inv.balanceAmount || '0'), 0);

  if (loading) {
    return <div className="loading">Loading transactions...</div>;
  }

  const compact = isResident();

  const formatDate = (invoice: Invoice) => {
    if (invoice.paymentTimestamp) {
      return new Date(invoice.paymentTimestamp).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }
    const period = `${invoice.paymentMonth || ''} ${invoice.paymentYear || ''}`.trim();
    return period || '—';
  };

  const typeBadgeClass = (invoice: Invoice) => {
    const t = (invoice.paymentType || 'RENT').toLowerCase().replace(/_/g, '-');
    return t;
  };

  return (
    <div className={`my-invoices${compact ? ' my-invoices--compact' : ''}`}>
      <h1>{compact ? 'Transactions' : 'My Invoices'}</h1>

      <div className="summary-cards">
        <div className="summary-card">
          <h3>{compact ? 'Paid' : 'Total Paid'}</h3>
          <p className="amount">₹{totalPaid.toLocaleString('en-IN')}</p>
        </div>
        <div className="summary-card">
          <h3>{compact ? 'Pending' : 'Pending Balance'}</h3>
          <p className="amount pending">₹{totalPending.toLocaleString('en-IN')}</p>
        </div>
        <div className="summary-card">
          <h3>{compact ? 'Count' : 'Total Invoices'}</h3>
          <p className="amount">{invoices.length}</p>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="no-data">No transactions found</div>
      ) : compact ? (
        <ul className="tx-list">
          {invoices.map((invoice) => {
            const guestInv = isGuestStayInvoice(invoice);
            const sum = guestInv ? guestInvoiceSummary(invoice) : null;
            const period = guestInv ? guestInvoicePeriodLabel(invoice) : null;
            const paid = parseFloat(String(invoice.paidAmount || 0));
            const balance = parseFloat(String(invoice.balanceAmount || 0));
            const discount = parseFloat(String(invoice.discount || 0));
            const typeClass = typeBadgeClass(invoice);
            return (
              <li key={invoice.id} className="tx-row">
                <div className="tx-row__info">
                  <div className="tx-row__title">
                    <span className="tx-row__id">#{invoice.id}</span>
                    <span className={`type-badge type-badge--sm ${typeClass}`}>
                      {paymentTypeLabel(invoice.paymentType)}
                    </span>
                  </div>
                  <span className="tx-row__meta">{formatDate(invoice)}</span>
                  {guestInv && period ? (
                    <span className="tx-row__meta">
                      {period.line1}
                      {period.line2 ? ` · ${period.line2}` : ''}
                    </span>
                  ) : null}
                </div>
                <div className="tx-row__amounts">
                  {guestInv && sum ? (
                    <>
                      <span className="tx-row__amt">Total {formatGuestMoney(sum.totalDue)}</span>
                      <span className="tx-row__amt tx-row__amt--paid">Paid {formatGuestMoney(sum.paid)}</span>
                      {sum.balance > 0 ? (
                        <span className="tx-row__amt tx-row__amt--due">
                          Due {formatGuestMoney(sum.balance)}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <span className="tx-row__amt tx-row__amt--paid">₹{paid.toLocaleString('en-IN')}</span>
                      {balance > 0 ? (
                        <span className="tx-row__amt tx-row__amt--due">Due ₹{balance.toLocaleString('en-IN')}</span>
                      ) : null}
                      {discount > 0 ? (
                        <span className="tx-row__amt tx-row__amt--disc">−₹{discount.toLocaleString('en-IN')}</span>
                      ) : null}
                    </>
                  )}
                </div>
                <button type="button" className="tx-row__btn" onClick={() => void handleDownload(invoice)}>
                  PDF
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="invoices-list">
          {invoices.map((invoice) => {
            const guestInv = isGuestStayInvoice(invoice);
            const sum = guestInv ? guestInvoiceSummary(invoice) : null;
            const period = guestInv ? guestInvoicePeriodLabel(invoice) : null;
            return (
            <div key={invoice.id} className="invoice-card">
              <div className="invoice-header">
                <div>
                  <h3>Invoice #{invoice.id}</h3>
                  <p className="invoice-date">{formatDate(invoice)}</p>
                </div>
                <span className={`type-badge ${typeBadgeClass(invoice)}`}>
                  {paymentTypeLabel(invoice.paymentType)}
                </span>
              </div>

              <div className="invoice-details">
                {guestInv && sum ? (
                  <>
                    <div className="detail-row">
                      <span>Daily rate:</span>
                      <span>{formatGuestMoney(sum.dailyRate)}/day</span>
                    </div>
                    <div className="detail-row">
                      <span>Number of days:</span>
                      <span>{sum.days}</span>
                    </div>
                    <div className="detail-row">
                      <span>Stay total:</span>
                      <span>{formatGuestMoney(sum.totalDue)}</span>
                    </div>
                    <div className="detail-row">
                      <span>Paid (this payment):</span>
                      <span className="paid">{formatGuestMoney(sum.paid)}</span>
                    </div>
                    <div className="detail-row">
                      <span>Balance remaining:</span>
                      <span className={sum.balance > 0 ? 'balance' : ''}>{formatGuestMoney(sum.balance)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="detail-row">
                      <span>Month/Year:</span>
                      <span>
                        {invoice.paymentMonth}/{invoice.paymentYear}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span>Paid Amount:</span>
                      <span className="paid">₹{parseFloat(invoice.paidAmount || '0').toLocaleString()}</span>
                    </div>
                    {parseFloat(invoice.balanceAmount || '0') > 0 && (
                      <div className="detail-row">
                        <span>Balance:</span>
                        <span className="balance">₹{parseFloat(invoice.balanceAmount || '0').toLocaleString()}</span>
                      </div>
                    )}
                    {invoice.discount && parseFloat(invoice.discount) > 0 && (
                      <div className="detail-row">
                        <span>Discount:</span>
                        <span className="discount">₹{parseFloat(invoice.discount).toLocaleString()}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="invoice-actions">
                <button type="button" onClick={() => void handleDownload(invoice)} className="download-btn">
                  Download PDF
                </button>
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
};

export default MyInvoices;
